/**
 * Client-side CSRF Protection Utilities
 * Handles CSRF token fetching and automatic inclusion in requests
 */

import React from 'react';
import config from '../config';

// CSRF token cache
let csrfTokenCache = null;
let tokenExpiry = null;

/**
 * Fetch CSRF token from server
 * @returns {Promise<string>} - CSRF token
 */
export const fetchCSRFToken = async () => {
  try {
    // Check if cached token is still valid
    if (csrfTokenCache && tokenExpiry && Date.now() < tokenExpiry) {
      return csrfTokenCache;
    }

    const response = await fetch(`${config.apiUrl}/api/csrf-token`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch CSRF token: ${response.status}`);
    }

    const data = await response.json();
    
    if (!data.success || !data.csrfToken) {
      throw new Error('Invalid CSRF token response');
    }

    // Cache the token
    csrfTokenCache = data.csrfToken;
    tokenExpiry = Date.now() + (data.expiresIn || 15 * 60 * 1000) - 60000; // Expire 1 minute early

    return csrfTokenCache;
  } catch (error) {
    console.error('Error fetching CSRF token:', error);
    throw error;
  }
};

/**
 * Get CSRF token from response headers
 * @param {Response} response - Fetch response object
 * @returns {string|null} - CSRF token from headers
 */
export const getCSRFTokenFromHeaders = (response) => {
  return response.headers.get('X-CSRF-Token');
};

/**
 * Enhanced fetch with automatic CSRF token handling
 * @param {string} url - Request URL
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} - Fetch response
 */
export const fetchWithCSRF = async (url, options = {}) => {
  const method = options.method || 'GET';
  
  // Skip CSRF for GET, HEAD, OPTIONS requests
  if (['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase())) {
    return fetch(url, options);
  }

  try {
    // Get CSRF token
    const csrfToken = await fetchCSRFToken();
    
    // Prepare headers
    const headers = {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-Token': csrfToken,
      ...options.headers
    };

    // Add CSRF token to body if it's form data
    let body = options.body;
    if (body && typeof body === 'object' && !(body instanceof FormData)) {
      if (typeof body === 'string') {
        try {
          const parsed = JSON.parse(body);
          parsed.csrfToken = csrfToken;
          body = JSON.stringify(parsed);
        } catch (e) {
          // If not JSON, leave as is
        }
      } else {
        body = JSON.stringify({
          ...body,
          csrfToken: csrfToken
        });
      }
    }

    // Make request
    const response = await fetch(url, {
      ...options,
      headers,
      body,
      credentials: 'include'
    });

    // Update token cache from response headers
    const newToken = getCSRFTokenFromHeaders(response);
    if (newToken) {
      csrfTokenCache = newToken;
      tokenExpiry = Date.now() + 15 * 60 * 1000 - 60000; // 15 minutes minus 1 minute buffer
    }

    return response;
  } catch (error) {
    console.error('Error in fetchWithCSRF:', error);
    throw error;
  }
};

/**
 * Create form data with CSRF token
 * @param {Object} data - Form data object
 * @returns {Promise<FormData>} - FormData with CSRF token
 */
export const createFormDataWithCSRF = async (data) => {
  const formData = new FormData();
  
  // Add CSRF token
  const csrfToken = await fetchCSRFToken();
  formData.append('csrfToken', csrfToken);
  
  // Add other data
  Object.keys(data).forEach(key => {
    if (data[key] !== null && data[key] !== undefined) {
      formData.append(key, data[key]);
    }
  });
  
  return formData;
};

/**
 * Axios interceptor for automatic CSRF token handling
 * @param {Object} axiosInstance - Axios instance
 */
export const setupAxiosCSRFInterceptor = (axiosInstance) => {
  // Request interceptor
  axiosInstance.interceptors.request.use(
    async (config) => {
      const method = config.method?.toUpperCase();
      
      // Skip CSRF for GET, HEAD, OPTIONS requests
      if (['GET', 'HEAD', 'OPTIONS'].includes(method)) {
        return config;
      }

      try {
        // Get CSRF token
        const csrfToken = await fetchCSRFToken();
        
        // Add token to headers
        config.headers['X-CSRF-Token'] = csrfToken;
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        
        // Add token to data if it's an object
        if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
          config.data = {
            ...config.data,
            csrfToken: csrfToken
          };
        }
        
        return config;
      } catch (error) {
        console.error('Error adding CSRF token to request:', error);
        return Promise.reject(error);
      }
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor to update token cache
  axiosInstance.interceptors.response.use(
    (response) => {
      // Update token cache from response headers
      const newToken = response.headers['x-csrf-token'];
      if (newToken) {
        csrfTokenCache = newToken;
        tokenExpiry = Date.now() + 15 * 60 * 1000 - 60000;
      }
      
      return response;
    },
    (error) => {
      // Handle CSRF token errors
      if (error.response?.status === 403 && 
          error.response?.data?.code?.includes('CSRF')) {
        console.warn('CSRF token error, clearing cache:', error.response.data);
        csrfTokenCache = null;
        tokenExpiry = null;
      }
      
      return Promise.reject(error);
    }
  );
};

/**
 * React hook for CSRF token management
 * @returns {Object} - { token, loading, error, refreshToken }
 */
export const useCSRFToken = () => {
  const [token, setToken] = React.useState(csrfTokenCache);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const refreshToken = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const newToken = await fetchCSRFToken();
      setToken(newToken);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (!token) {
      refreshToken();
    }
  }, [token]);

  return {
    token,
    loading,
    error,
    refreshToken
  };
};

/**
 * Higher-order component for CSRF protection
 * @param {Component} WrappedComponent - Component to wrap
 * @returns {Component} - Wrapped component with CSRF protection
 */
export const withCSRFProtection = (WrappedComponent) => {
  return function CSRFProtectedComponent(props) {
    const { token, loading, error, refreshToken } = useCSRFToken();
    
    if (loading) {
      return <div>Loading security token...</div>;
    }
    
    if (error) {
      return (
        <div>
          <p>Security token error: {error.message}</p>
          <button onClick={refreshToken}>Retry</button>
        </div>
      );
    }
    
    return <WrappedComponent {...props} csrfToken={token} />;
  };
};

/**
 * Clear CSRF token cache
 */
export const clearCSRFTokenCache = () => {
  csrfTokenCache = null;
  tokenExpiry = null;
};

/**
 * Get current CSRF token from cache
 * @returns {string|null} - Current token or null
 */
export const getCurrentCSRFToken = () => {
  if (csrfTokenCache && tokenExpiry && Date.now() < tokenExpiry) {
    return csrfTokenCache;
  }
  return null;
};

export default {
  fetchCSRFToken,
  fetchWithCSRF,
  createFormDataWithCSRF,
  setupAxiosCSRFInterceptor,
  useCSRFToken,
  withCSRFProtection,
  clearCSRFTokenCache,
  getCurrentCSRFToken
}; 